import React, { useContext, useState } from 'react';
import { AddCarrierUserInDTO } from '@api/ringil-api.v2.tsx';

import * as yup from 'yup';
import { findValidationMessage } from '@utils/validation';
import { ApiContext } from '@api/api';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { ColWithGap } from '@components/styles.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import useForm from '@hooks/useForm/useForm.tsx';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onComplete: () => void;
  carrierId: string;
}

export const AddCarrierUserSidebar: React.FC<ComponentProps> = ({ visible, onHide, onComplete, carrierId }) => {
  const { tr } = useTranslationLgs();
  const { cuCreateCarrierUserByCustomer } = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const { setBeValidationErrors, BackendValidationComponent } = useBEValidation();

  const schema = yup.object().shape({
    firstname: yup.string().required(),
    surname: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string().required(),
  });

  const formInitialValues = {
    firstname: '',
    surname: '',
    email: '',
    phone: '',
  };

  const onContinueHandler = (data: AddCarrierUserInDTO) => {
    setLoading(true);
    cuCreateCarrierUserByCustomer(
      carrierId,
      data,
      () => {
        setLoading(false);
        onComplete();
      },
      null,
      {
        onValidationFailed: e => {
          setLoading(false);
          setBeValidationErrors(e);
        },
      },
    );
  }

  const form = useForm<AddCarrierUserInDTO>(
    schema,
    formInitialValues,
    (d) => onContinueHandler(d),
    false,
    false,
    () => {
    },
    'none',
  );

  return (
    <RingilSidebar
      heading={tr('AddCarrierUserSidebar.createUser', 'Add user')}
      visible={visible}
      onBack={onHide}
      onContinue={() => form.validateAndSend()}
      loading={loading}
    >
        <BackendValidationComponent />
      <ColWithGap>
        <Field
          label={tr('AddCarrierUserSidebar.firstName', 'First name')}
          required
          errorMessage={findValidationMessage(form.validationErrors, form.names.firstname, tr)}
        >
          <RingilInputText
            value={form.values.firstname}
            onChange={value => form.setFieldValue('firstname', value)}
            fullWidth
          />
        </Field>
        <Field
          label={tr('AddCarrierUserSidebar.surname', 'Surname')}
          required
          errorMessage={findValidationMessage(form.validationErrors, form.names.surname, tr)}
        >
          <RingilInputText
            value={form.values.surname}
            onChange={value => form.setFieldValue('surname', value)}
            fullWidth
          />
        </Field>
        <Field
          label={tr('AddCarrierUserSidebar.email', 'Email')}
          required
          errorMessage={findValidationMessage(form.validationErrors, form.names.email, tr)}
        >
          <RingilInputText
            value={form.values.email}
            onChange={value => form.setFieldValue('email', value)}
            fullWidth
          />
        </Field>
        <Field
          label={tr('AddCarrierUserSidebar.phone', 'Phone')}
          required
          errorMessage={findValidationMessage(form.validationErrors, form.names.phone, tr)}
        >
          <RingilInputText
            value={form.values.phone}
            onChange={value => form.setFieldValue('phone', value)}
            fullWidth
          />
        </Field>
      </ColWithGap>
    </RingilSidebar>
  );
};
